import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Modal, Row, Col, Button } from 'react-bootstrap';
import exitIntent from 'exit-intent-quattro';

import ReactGA4 from 'react-ga4';

import cookie from '../../../../util/cookie.js';
import storage from '../../../../util/storage.js';
import config from '../../../../config.js';

import { getCmsPanelRequest, memoizedGetPanelBySlugAndLanguage, getContentBlockItemsByName, getContentBlockMixedItem, getContentBlockItemValue } from '../../../Cms/CmsActions.js';
import { getCartItems } from '../../../Cart/CartActions.js';
import { getProducts, getProductSpecialPriceDateThreshold } from '../../../Product/ProductActions.js';
import { getUserGroup, USER_GROUP_DEFAULT } from '../../../User/UserGroupActions.js';
import { getLoggedUser } from '../../../User/UserActions.js';
import { isPopinActive, setPopinActive } from '../../AppActions.js';

import withRouter from '../../../../components/Router/WithRouter.js';
import Icon from '../../../../components/Content/Icon.js';
import HtmlComponent from '../../../../components/Text/HtmlComponent.js';
import StarRating from '../../../Review/components/view/StarRating.js';
import Chrono from '../../../../components/Time/Chrono.js';
import GamifiedButton from '../../../../components/Content/GamifiedButton.js';
import { applyVoucher, applyVoucherRequest } from '../../../Voucher/VoucherActions.js';

const SESSION_KEY = 'exitIntentDeadline';
const PANEL_SLUG = 'exit-intent';

function toNativeDate(d) {
    if(!d) return null;
    if(d instanceof Date) return d;
    if(typeof d.toDate === 'function') return d.toDate(); // dayjs object
    if(typeof d === 'string') return new Date(d);
    return null;
}

function resolveDeadline(products) {
    const now = Date.now();
    const futures = (products || [])
        .flatMap(p => (p.specialPrices || []))
        .map(sp => toNativeDate(getProductSpecialPriceDateThreshold(sp)))
        .filter(d => d instanceof Date && !Number.isNaN(d.getTime()) && d.getTime() > now)
        .sort((a, b) => a - b);

    if(futures.length > 0) return futures[0];

    try {
        const stored = storage.getItem(SESSION_KEY);
        if(stored) {
            const d = new Date(stored);
            if(d.getTime() > now) return d;
        }
    } catch (e) { /* sessionStorage may be blocked */ }
    const deadline = new Date(now + 60 * 60 * 1000);
    try {
		storage.setItem(SESSION_KEY, deadline.toISOString());
	} catch (e) { /* sessionStorage may be blocked */ }
    return deadline;
}

function ExitIntent({ dispatch, intl, user = null, userGroup = null, cartItems = [], products = [], popinActive = false, panel = null }) {
    const [isDisplayed, setIsDisplayed] = useState(false);
    const [modalAlreadyDisplayed, setModalAlreadyDisplayed] = useState(false);
    const [deadline, setDeadline] = useState(null);
    const [successPromo, setSuccessPromo] = useState(null);

    useEffect(() => {
        isDisplayed && !modalAlreadyDisplayed && dispatch(getCmsPanelRequest(PANEL_SLUG, intl.locale));
    }, [isDisplayed]);

    useEffect(() => {
        if(cookie.getItem('exitIntent')) {
            setModalAlreadyDisplayed(true);
        } else {
            exitIntent({
                edges: { top: true, right: false, bottom: false, left: false },
                threshold: 5,
                maxDisplays: 5,
                eventThrottle: 100,
                onExitIntent: () => {
                    if(!popinActive && !modalAlreadyDisplayed) {
                        setIsDisplayed(true);
                    }
                },
            });
        }
    }, []);

    useEffect(() => {
        if(isDisplayed && !deadline) {
            setDeadline(resolveDeadline(products));
        }
    }, [isDisplayed]);

    const toggle = (event, display = null) => {
        const displayModal = display !== null ? display : !isDisplayed;
        setIsDisplayed(displayModal);
        dispatch(setPopinActive(displayModal));
        if(!displayModal) {
            setModalAlreadyDisplayed(true);
            process.env.NODE_ENV === 'production' && cookie.setItem('exitIntent', 1, { maxAge: 24 * 60 * 60 });
        }
    };

    const getNextStepLink = () => (cartItems.length ? `/${intl.locale}/cart` : '#');
    const hasCart = cartItems.length > 0;

    const promos = getContentBlockItemsByName(panel?.blocks, 'promo');
    const promo1 = promos[0] || null;
    const promo2 = promos[1] || null;
    const promo1Html = getContentBlockMixedItem(promo1, 'content')?.text || '';
    const promo1Code = getContentBlockMixedItem(promo1, 'code')?.text || '';
    const promo2Html = getContentBlockMixedItem(promo2, 'content')?.text || '';
    const promo2Code = getContentBlockMixedItem(promo2, 'code')?.text || '';
    const extraHtml = getContentBlockItemValue(panel?.blocks, 'extra');

    const handlePromoClick = (promoNum, promoCode) => {
        if(typeof window !== 'undefined' && config.google.analytics.trackingCodeGA4) {
            ReactGA4.event('select_promotion', {
                creative_name: `exit_intent_promo${promoNum}`,
                creative_slot: 'exit_intent',
                promotion_id: promoCode || 'NC',
                promotion_name: `exit_intent_promo${promoNum}`,
            });
        }
		promoCode && dispatch(applyVoucherRequest(promoCode)).catch(console.error);
        setSuccessPromo(promoNum);
        setTimeout(() => toggle(null, false), 2000);
    };

    if(
        !modalAlreadyDisplayed
        && (!user || process.env.NODE_ENV === 'development')
        && userGroup?.identifier === USER_GROUP_DEFAULT
    ) {
        return (
            <Modal size="lg" show={isDisplayed} onHide={toggle} centered contentClassName="overflow-hidden p-0">

                <div className="bg-danger-gradient text-white p-4 text-center">
                    <small className="d-block text-uppercase opacity-75 mb-1" style={{ letterSpacing: '0.1em', fontSize: '0.7rem' }}>
                        <FormattedMessage id="exitIntentEyebrow" defaultMessage="Leaving already?" />
                    </small>
                    <p className="fs-4 fw-semibold mb-0">
                        <FormattedMessage id="exitIntentHeadline" defaultMessage="Don't leave without your gifts!" />
                    </p>
                    {deadline && (
                        <div className="d-inline-flex align-items-center gap-1 mt-3 rounded-1 px-2 py-1 small bg-white-gradient text-danger">
                            <Icon icon="time" />&nbsp;<FormattedMessage id="exitIntentTimerLabel" defaultMessage="Offers reserved for" />&nbsp;<strong className="font-monospace"><Chrono date={deadline} hideOnEnd onEnd={() => setDeadline(null)} /></strong>
                        </div>
                    )}
                </div>

                <div className="bg-info bg-texture p-3 pb-2">
                    <Row className="g-2 mb-3">
                        <Col xs="12" md="5">
                            <div className="bg-white border rounded p-3 h-100 text-center shadow d-flex flex-column">
                                <div className="flex-grow-1"><HtmlComponent text={promo1Html} /></div>
                                {successPromo === 1 ? (
                                    <p className="text-success fw-semibold mb-0 mt-2 small">
                                        <FormattedMessage id="exitIntentPromoSuccess" defaultMessage="Great choice! 🎉" />
                                    </p>
                                ) : (
                                    <GamifiedButton as={Button} variant="danger" size="sm" className="mt-2 w-100" onClick={() => handlePromoClick(1, promo1Code)}>
                                        <FormattedMessage id="exitIntentCtaPromo" defaultMessage="I take this offer" />
                                    </GamifiedButton>
                                )}
								{process.env.NODE_ENV === 'development' && (
									<small className="text-muted mt-2">{promo1Code}</small>
								)}
                            </div>
                        </Col>
                        <Col xs="12" md="2" className="d-flex align-items-center justify-content-center py-2 py-md-0">
                            <span className="text-secondary fw-bold"><FormattedMessage id="exitIntentOr" defaultMessage="OR" /></span>
                        </Col>
                        <Col xs="12" md="5">
                            <div className="bg-white border rounded p-3 h-100 text-center shadow d-flex flex-column">
                                <div className="flex-grow-1"><HtmlComponent text={promo2Html} /></div>
                                {successPromo === 2 ? (
                                    <p className="text-success fw-semibold mb-0 mt-2 small">
                                        <FormattedMessage id="exitIntentPromoSuccess" defaultMessage="Great choice! 🎉" />
                                    </p>
                                ) : (
                                    <GamifiedButton as={Button} variant="danger" size="sm" className="mt-2 w-100" onClick={() => handlePromoClick(2, promo2Code)}>
                                        <FormattedMessage id="exitIntentCtaPromo" defaultMessage="I take this offer" />
                                    </GamifiedButton>
                                )}
								{process.env.NODE_ENV === 'development' && (
									<small className="text-muted mt-2">{promo2Code}</small>
								)}
                            </div>
                        </Col>
                    </Row>

                    <HtmlComponent text={extraHtml} />
                </div>

                <div className="p-3 text-center">
                    <Button
                        variant="outline-danger"
                        size="lg"
                        className="w-100 mb-2 fw-semibold"
                        as={Link}
                        to={getNextStepLink()}
                        onClick={toggle}
                    >
                        {hasCart ? <FormattedMessage id="exitIntentCtaCart" defaultMessage="Complete my order and get my gifts 🛒" /> : <FormattedMessage id="exitIntentCtaBrowse" defaultMessage="🛍️ Browse products and enjoy the offers" />}
                    </Button>

                    <StarRating centered />

                    <Button
                        size="sm"
                        variant="link"
                        className="w-100 text-muted mt-1"
                        onClick={toggle}
                    >
                        <FormattedMessage id="exitIntentCtaDismiss" defaultMessage="No thanks, I'll leave without my gifts" />
                    </Button>
                </div>

            </Modal>
        );
    }

    if(process.env.NODE_ENV === 'development') {
        return <Button onClick={toggle}>Display exit intent popin {modalAlreadyDisplayed && 'already displayed'} {userGroup?.identifier !== USER_GROUP_DEFAULT && 'group not allowed'}</Button>;
    }

    return null;
}

function mapStateToProps(store) {
    return {
        user: getLoggedUser(store),
        userGroup: getUserGroup(store),
        cartItems: getCartItems(store),
        products: getProducts(store),
        popinActive: isPopinActive(store),
        panel: memoizedGetPanelBySlugAndLanguage(store, PANEL_SLUG, store.intl.locale),
    };
}

ExitIntent.propTypes = {
    dispatch: PropTypes.func.isRequired,
    intl: PropTypes.shape({ locale: PropTypes.string.isRequired }).isRequired,
    user: PropTypes.shape({}),
    userGroup: PropTypes.shape({}),
    cartItems: PropTypes.arrayOf(PropTypes.shape({})),
    products: PropTypes.arrayOf(PropTypes.shape({})),
    popinActive: PropTypes.bool,
    panel: PropTypes.shape({}),
};

ExitIntent.defaultProps = {
    user: null,
    userGroup: null,
    cartItems: [],
    products: [],
    popinActive: false,
    panel: null,
};

export default connect(mapStateToProps)(withRouter(injectIntl(ExitIntent)));
